/**
 * ProjectRMWWorkspace — the Project Manager's editable risk worksheet.
 *
 * The worksheet is grouped per Risk Area in MUI Accordions. Each accordion
 * contains an editable table of risks where the PM can adjust Impact (1-10),
 * Probability (1-10), Mitigation, Target Date and Status inline. Exposure
 * and Level are recomputed live client-side using the same formula the
 * backend uses (see RiskScoreBadge.ts) so the chip color flips in real time
 * as the PM scrubs values.
 *
 * Actions:
 *   - **Draft save** — persists snapshots for the current OPEN cycle.
 *   - **Submit review** — calls `cycles/:id/submit/`.
 *   - **Open new cycle** — POST to /cycles/ with a `carry_forward_open_risks`
 *     flag (default true) so the new cycle starts pre-populated with the
 *     latest snapshot of every still-open risk.
 *   - **Add risk** — opens a small dialog to pick area + contributor and
 *     persist a new Risk row.
 *
 * View-only fallbacks render when the user is not the assigned PM or an
 * RMW admin (Process Compliance Officer, Employee on the project).
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HistoryIcon from "@mui/icons-material/History";
import TimelineIcon from "@mui/icons-material/Timeline";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import PageTitle from "../../../components/Page Component/PageTitle";
import { fetchData, patchData, postData } from "../../../store/actions";
import { RootState } from "../../../store/Store";
import { useCustomToast } from "../../../components/Methods/SnackBar";
import {
  riskCyclesUrl,
  riskDetailUrl,
  risksUrl,
  riskSnapshotsUrl,
  riskTemplateDetailUrl,
  riskWorksheetDetailUrl,
  submitRiskCycleUrl,
} from "../../../Service/Apis";
import {
  Risk,
  RiskArea,
  RiskContributor,
  RiskSnapshot,
  RiskStatus,
  RiskTemplate,
  RiskWorksheet,
} from "./Types/RiskManagementTypes";
import RiskScoreBadge, {
  computeExposure,
  levelFromExposure,
} from "./components/RiskScoreBadge";
import { isRmwAdmin } from "./components/rmwRoles";
import { getDefaultFactors } from "./components/defaultFactors";
import {
  normalizeRiskList,
  normalizeWorksheet,
} from "./components/normalizers";
import SkillDocumentCommentBox from "../../../components/SkillDocumentCommentBox/SkillDocumentCommentBox";

interface EditableRiskRow {
  risk: Risk;
  snapshot: RiskSnapshot | null;
  draftImpact: number;
  draftProbability: number;
  draftMitigation: string;
  draftTargetDate: string;
  draftStatus: RiskStatus;
  draftComments: string;
  draftRatingFactors: string;
  draftScenarioJustification: string;
  isDirty: boolean;
}

const STATUS_OPTIONS: { value: RiskStatus; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "mitigated", label: "Mitigated" },
  { value: "closed", label: "Closed" },
  { value: "reappeared", label: "Reappeared" },
];

/** Suggest a sensible cycle name given the frequency and today's date. */
const suggestCycleName = (
  frequency: string | undefined,
  date: Date = new Date()
): string => {
  const month = date.toLocaleString("default", { month: "long" });
  const year = date.getFullYear();
  if (frequency === "quarterly") {
    const q = Math.floor(date.getMonth() / 3) + 1;
    return `Q${q} ${year}`;
  }
  return `${month} ${year}`;
};

const toDateInputValue = (iso?: string | null) => (iso ? iso.slice(0, 10) : "");

const buildRowFromRisk = (risk: Risk): EditableRiskRow => {
  const snap = risk.current_snapshot ?? null;
  return {
    risk,
    snapshot: snap,
    draftImpact: Number(snap?.impact ?? 1) || 1,
    draftProbability: Number(snap?.probability ?? 1) || 1,
    draftMitigation: snap?.mitigation_plan ?? "",
    draftTargetDate: toDateInputValue(snap?.target_date ?? null),
    draftStatus: snap?.status ?? "open",
    draftComments: snap?.comments ?? "",
    draftRatingFactors: snap?.rating_factors ?? "",
    draftScenarioJustification: snap?.scenario_justification ?? "",
    isDirty: false,
  };
};

/**
 * Small controlled input for 1-10 bounded integers (Impact / Probability).
 *
 * Why a dedicated component? The previous inline implementation re-clamped
 * the value on every keystroke and wrote it straight back to React state.
 * That created a few subtle ways to end up one digit short when typing
 * "10" — most notably typing the second digit while the controlled value
 * was already being reset, so users would see `8 → 10` get saved as `9`.
 *
 * This input tracks the raw string locally while the user is editing and
 * only commits a clamped integer to the parent on blur / Enter / when the
 * outside value changes. The committed value is always integer-rounded
 * and clamped to the [min, max] range.
 */
interface BoundedNumberInputProps {
  value: number;
  min: number;
  max: number;
  disabled?: boolean;
  width?: number;
  onCommit: (next: number) => void;
}

const BoundedNumberInput: React.FC<BoundedNumberInputProps> = ({
  value,
  min,
  max,
  disabled,
  width = 50,
  onCommit,
}) => {
  const [draft, setDraft] = useState<string>(String(value));

  // Keep the local string in sync when the parent value changes from
  // outside (initial load, refetch after save, etc.).
  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = () => {
    const parsed = Math.round(Number(draft));
    const safe = Number.isFinite(parsed)
      ? Math.min(max, Math.max(min, parsed))
      : min;
    if (safe !== value) onCommit(safe);
    setDraft(String(safe));
  };

  return (
    <TextField
      size="small"
      type="number"
      value={draft}
      disabled={disabled}
      inputProps={{ min, max, step: 1, style: { width } }}
      onChange={(e) => {
        // Accept any digit string while typing — clamping happens on
        // commit so typing "10" (i.e. press "1" then "0") doesn't lose
        // the second keystroke to a mid-edit clamp.
        const v = e.target.value;
        if (v === "" || /^-?\d+$/.test(v)) setDraft(v);
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          commit();
          (e.target as HTMLInputElement).blur();
        }
      }}
    />
  );
};

const ProjectRMWWorkspace: React.FC = () => {
  const { worksheetId } = useParams<{ worksheetId: string }>();
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const selectedRole = useSelector(
    (state: RootState) => state.auth.selectedRole
  );
  const userId = useSelector((state: RootState) => state.auth.userId);

  const [worksheet, setWorksheet] = useState<RiskWorksheet | null>(null);
  const [template, setTemplate] = useState<RiskTemplate | null>(null);
  const [risks, setRisks] = useState<Risk[]>([]);
  const [rowsByArea, setRowsByArea] = useState<Record<string, EditableRiskRow[]>>(
    {}
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Add-risk dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addArea, setAddArea] = useState<string>("");
  const [addContributor, setAddContributor] = useState<string>("");
  const [addDescription, setAddDescription] = useState<string>("");
  const [addSaving, setAddSaving] = useState(false);

  // Expandable detail rows
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const toggleRowExpanded = useCallback((riskId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(riskId)) next.delete(riskId);
      else next.add(riskId);
      return next;
    });
  }, []);

  // New-cycle dialog
  const [cycleOpen, setCycleOpen] = useState(false);
  const [cycleName, setCycleName] = useState("");
  const [cycleStart, setCycleStart] = useState("");
  const [cycleEnd, setCycleEnd] = useState("");
  const [carryForward, setCarryForward] = useState(true);
  const [cycleSaving, setCycleSaving] = useState(false);

  const isAdmin = isRmwAdmin(selectedRole);
  // Compare as strings — backend user IDs may come back as int or UUID, while
  // `userId` is the string we stored in localStorage at login. A mismatched
  // type was silently disabling the edit controls for the actual PM.
  const isAssignedPM = useMemo(
    () =>
      !!userId &&
      worksheet?.current_pm != null &&
      String(worksheet.current_pm) === String(userId),
    [userId, worksheet]
  );
  // Only the assigned PM can edit risk data (impact, probability, status,
  // mitigation, etc.). Admins retain administrative controls (open/close
  // cycles, lock worksheets) but cannot modify risk row content.
  const canEdit =
    !!worksheet &&
    worksheet.is_active &&
    !worksheet.is_locked &&
    isAssignedPM &&
    (worksheet.current_cycle_status === "open" ||
      worksheet.current_cycle_status === "draft" ||
      worksheet.current_cycle_status === "submitted");
  const isCycleSubmitted = worksheet?.current_cycle_status === "submitted";

  // -------------------------------------------------------------------------
  // Data loading
  // -------------------------------------------------------------------------

  const fetchWorksheet = useCallback(async () => {
    if (!worksheetId) return;
    setLoading(true);
    try {
      const wsRaw = await dispatch(fetchData(riskWorksheetDetailUrl(worksheetId)));
      const ws = normalizeWorksheet(wsRaw);
      setWorksheet(ws);
      const [tpl, rls] = await Promise.all([
        ws.template
          ? dispatch(fetchData(riskTemplateDetailUrl(ws.template)))
          : Promise.resolve(null),
        // Ask the API to omit closed risks — the worksheet editor only deals
        // with the currently-active set. Belt-and-suspenders: we also filter
        // on the client below so legacy backends that ignore the query param
        // still render correctly.
        dispatch(
          fetchData(
            `${risksUrl}?worksheet=${worksheetId}&is_closed=false&page_size=500`
          )
        ),
      ]);
      setTemplate(tpl as RiskTemplate);
      const normalized = normalizeRiskList(rls);
      setRisks(normalized.filter((r) => !r.is_closed));
    } catch {
      showToast("Failed to load worksheet.", "error", false);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worksheetId, dispatch]);

  useEffect(() => {
    fetchWorksheet();
  }, [fetchWorksheet]);

  // Rebuild editable row buffer whenever risks change.
  // When a risk's rating_factors snapshot is empty, pre-populate with the
  // template contributor's default factors so the PM has a starting point.
  // IMPORTANT: Preserve any dirty (unsaved) draft values so that adding a
  // new risk (which triggers a refetch) doesn't discard the user's edits.
  useEffect(() => {
    if (!template) return;
    setRowsByArea((prev) => {
      const dirtyLookup = new Map<string, EditableRiskRow>();
      Object.values(prev).forEach((rows) =>
        rows.forEach((r) => {
          if (r.isDirty) dirtyLookup.set(r.risk.id, r);
        })
      );

      const grouped: Record<string, EditableRiskRow[]> = {};
      template.areas.forEach((a) => {
        grouped[a.id] = [];
      });
      risks.forEach((r) => {
        const existing = dirtyLookup.get(r.id);
        if (existing) {
          const bucket = grouped[r.area] ?? [];
          bucket.push({ ...existing, risk: r });
          grouped[r.area] = bucket;
          return;
        }
        const row = buildRowFromRisk(r);
        if (!row.draftRatingFactors) {
          const area = template.areas.find((a) => a.id === r.area);
          if (area) {
            const contrib = area.contributors.find((c) => c.id === r.contributor);
            const raw = contrib?.factors || (contrib ? getDefaultFactors(area.name, contrib.name) : "");
            if (raw) {
              const lines = raw.split("\n").filter((f) => f.trim());
              if (lines.length) {
                row.draftRatingFactors = `<ul>${lines.map((f) => `<li><p>${f}</p></li>`).join("")}</ul>`;
              }
            }
          }
        }
        const bucket = grouped[r.area] ?? [];
        bucket.push(row);
        grouped[r.area] = bucket;
      });
      return grouped;
    });
  }, [risks, template]);

  // -------------------------------------------------------------------------
  // Helpers — derived per-area aggregates
  // -------------------------------------------------------------------------

  const areaAggregate = (areaId: string) => {
    const rows = rowsByArea[areaId] ?? [];
    if (!rows.length) return { avg: 0, level: "LOW" as const, total: 0, high: 0 };
    let total = 0;
    let high = 0;
    rows.forEach((row) => {
      const exposure = computeExposure(row.draftImpact, row.draftProbability);
      total += exposure;
      if (levelFromExposure(exposure) === "HIGH") high += 1;
    });
    const avg = total / rows.length;
    return { avg, level: levelFromExposure(avg), total: rows.length, high };
  };

  const overallExposure = useMemo(() => {
    let sum = 0;
    let count = 0;
    Object.values(rowsByArea).forEach((rows) => {
      rows.forEach((row) => {
        sum += computeExposure(row.draftImpact, row.draftProbability);
        count += 1;
      });
    });
    return count ? sum / count : 0;
  }, [rowsByArea]);

  const overallLevel = useMemo(
    () => levelFromExposure(overallExposure),
    [overallExposure]
  );

  // -------------------------------------------------------------------------
  // Row editing
  // -------------------------------------------------------------------------

  const updateRow = (
    areaId: string,
    riskId: string,
    patch: Partial<EditableRiskRow>
  ) => {
    setRowsByArea((prev) => {
      const rows = prev[areaId] ?? [];
      return {
        ...prev,
        [areaId]: rows.map((r) =>
          r.risk.id === riskId ? { ...r, ...patch, isDirty: true } : r
        ),
      };
    });
  };

  const dirtyRows = useMemo(() => {
    const out: EditableRiskRow[] = [];
    Object.values(rowsByArea).forEach((rows) =>
      rows.forEach((r) => r.isDirty && out.push(r))
    );
    return out;
  }, [rowsByArea]);

  // -------------------------------------------------------------------------
  // Save / Submit
  // -------------------------------------------------------------------------

  const clearDirtyFlags = useCallback(() => {
    setRowsByArea((prev) => {
      const next: Record<string, EditableRiskRow[]> = {};
      Object.entries(prev).forEach(([areaId, rows]) => {
        next[areaId] = rows.map((r) =>
          r.isDirty ? { ...r, isDirty: false } : r
        );
      });
      return next;
    });
  }, []);

  const handleDraftSave = async (): Promise<boolean> => {
    if (!worksheet?.current_cycle_id) {
      showToast("No open cycle to save into.", "error", false);
      return false;
    }
    // Force the currently-focused input (if any) to commit its draft value
    // before we snapshot dirtyRows. Without this, clicking Save while still
    // typing in an Impact/Probability cell could read a stale value because
    // the input commits on blur and the click might race that blur.
    (document.activeElement as HTMLElement | null)?.blur?.();
    // Yield to React so the state update from the blur-driven commit
    // flushes into `rowsByArea` (and therefore `dirtyRows`) before we read
    // it below.
    await new Promise((resolve) => setTimeout(resolve, 0));
    if (!dirtyRows.length) {
      showToast("Nothing to save.", "info", false);
      return true;
    }
    setSaving(true);
    try {
      await Promise.all(
        dirtyRows.map((row) => {
          const payload = {
            risk: row.risk.id,
            cycle: worksheet.current_cycle_id,
            impact: row.draftImpact,
            probability: row.draftProbability,
            mitigation_plan: row.draftMitigation,
            target_date: row.draftTargetDate || null,
            status: row.draftStatus,
            comments: row.draftComments,
            rating_factors: row.draftRatingFactors,
            scenario_justification: row.draftScenarioJustification,
          };
          if (row.snapshot?.id) {
            return dispatch(
              patchData(`${riskSnapshotsUrl}${row.snapshot.id}/`, payload)
            );
          }
          return dispatch(postData(riskSnapshotsUrl, payload));
        })
      );
      showToast("Draft saved.", "success", false);
      clearDirtyFlags();
      fetchWorksheet();
      return true;
    } catch {
      showToast("Failed to save some snapshots.", "error", false);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!worksheet?.current_cycle_id) return;
    // Make it explicit that submission is a soft milestone — the cycle stays
    // open and editing remains possible until an RMW Admin closes it. PMs may
    // re-submit any number of times to flag fresh edits for reviewers.
    const ok = window.confirm(
      isCycleSubmitted
        ? "Re-submit this cycle?\n\n" +
            "This re-stamps the cycle's submitted timestamp so reviewers " +
            "know there are new edits to look at. The cycle stays OPEN and " +
            "you can keep editing afterwards."
        : "Submit this cycle for review?\n\n" +
            "This stamps the cycle as 'submitted' and notifies reviewers, but the " +
            "cycle stays OPEN — you (and the admin) can still edit risks, add new " +
            "ones, and update snapshots. The cycle is only locked when an RMW " +
            "Admin runs the explicit Close action."
    );
    if (!ok) return;
    setSubmitting(true);
    try {
      // Mirror the safety net in `handleDraftSave`: blur any focused input so
      // a draft value the user typed but never committed (no Tab / no click
      // outside) lands in `rowsByArea` before we look at `dirtyRows`.
      (document.activeElement as HTMLElement | null)?.blur?.();
      await new Promise((resolve) => setTimeout(resolve, 0));
      // Save any pending edits first so submit is atomic with the UI state.
      if (dirtyRows.length) {
        const saved = await handleDraftSave();
        if (!saved) {
          setSubmitting(false);
          return;
        }
      }
      await dispatch(postData(submitRiskCycleUrl(worksheet.current_cycle_id), {}));
      showToast(
        isCycleSubmitted
          ? "Cycle re-submitted. Reviewers will see the latest edits."
          : "Review submitted. Cycle remains open for further edits.",
        "success",
        false
      );
      fetchWorksheet();
    } catch {
      showToast("Failed to submit cycle.", "error", false);
    } finally {
      setSubmitting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Add risk
  // -------------------------------------------------------------------------

  const openAdd = (areaId?: string) => {
    setAddArea(areaId ?? "");
    setAddContributor("");
    setAddDescription("");
    setAddOpen(true);
  };

  const contributorOptions: RiskContributor[] = useMemo(() => {
    const area = template?.areas.find((a) => a.id === addArea);
    return area?.contributors ?? [];
  }, [template, addArea]);

  const handleAddRisk = async () => {
    if (!worksheetId || !addArea || !addContributor || !addDescription.trim()) {
      showToast("Area, contributor and description are required.", "error", false);
      return;
    }
    setAddSaving(true);
    try {
      await dispatch(
        postData(risksUrl, {
          worksheet: worksheetId,
          area: addArea,
          contributor: addContributor,
          description: addDescription.trim(),
        })
      );
      showToast("Risk added.", "success", false);
      setAddOpen(false);
      fetchWorksheet();
    } catch {
      showToast("Failed to add risk.", "error", false);
    } finally {
      setAddSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // Open new cycle
  // -------------------------------------------------------------------------

  const openCycleDialog = () => {
    const suggested = suggestCycleName(worksheet?.review_frequency);
    setCycleName(suggested);
    const today = new Date();
    setCycleStart(today.toISOString().slice(0, 10));
    const end = new Date(today);
    end.setMonth(
      end.getMonth() + (worksheet?.review_frequency === "quarterly" ? 3 : 1)
    );
    setCycleEnd(end.toISOString().slice(0, 10));
    setCarryForward(true);
    setCycleOpen(true);
  };

  const handleCreateCycle = async () => {
    if (!worksheetId || !cycleName || !cycleStart || !cycleEnd) {
      showToast("All cycle fields are required.", "error", false);
      return;
    }
    setCycleSaving(true);
    try {
      await dispatch(
        postData(riskCyclesUrl, {
          worksheet: worksheetId,
          name: cycleName,
          period_start: cycleStart,
          period_end: cycleEnd,
          carry_forward_open_risks: carryForward,
        })
      );
      showToast("New review cycle opened.", "success", false);
      setCycleOpen(false);
      fetchWorksheet();
    } catch {
      showToast("Failed to open new cycle.", "error", false);
    } finally {
      setCycleSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // Delete (close) a risk
  // -------------------------------------------------------------------------

  const handleCloseRisk = async (risk: Risk) => {
    if (!window.confirm(`Close risk "${risk.description.slice(0, 60)}"?`)) return;
    try {
      await dispatch(
        patchData(riskDetailUrl(risk.id), {
          is_closed: true,
          closed_reason: "Closed from worksheet",
        })
      );
      // Optimistically drop the row from local state so the table updates
      // immediately, before the background refetch returns.
      setRisks((prev) => prev.filter((r) => r.id !== risk.id));
      setRowsByArea((prev) => {
        const next: Record<string, EditableRiskRow[]> = {};
        Object.entries(prev).forEach(([areaId, rows]) => {
          next[areaId] = rows.filter((r) => r.risk.id !== risk.id);
        });
        return next;
      });
      showToast("Risk closed.", "success", false);
      fetchWorksheet();
    } catch {
      showToast("Failed to close risk.", "error", false);
    }
  };

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  const renderHeader = () => {
    if (!worksheet) return null;
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography variant="overline" color="text.secondary">
                Project
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {worksheet.project_name ?? worksheet.project}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                PM: <b>{worksheet.current_pm_name ?? "Unassigned"}</b> •{" "}
                Template: {worksheet.template_name ?? "—"} •{" "}
                Frequency: {worksheet.review_frequency}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="overline" color="text.secondary">
                Current Cycle
              </Typography>
              <Typography variant="h6">
                {worksheet.current_cycle_name ?? "No open cycle"}
              </Typography>
              {/* A "submitted" cycle is still editable until an admin closes
                  it, so we show OPEN to avoid implying the cycle is locked.
                  We tack on a small tick when the PM has submitted at least
                  once, so the milestone is still visible. */}
              <Chip
                size="small"
                label={
                  worksheet.current_cycle_status === "submitted"
                    ? "OPEN ✓ submitted"
                    : (worksheet.current_cycle_status ?? "—").toUpperCase()
                }
                color={
                  worksheet.current_cycle_status === "open" ||
                  worksheet.current_cycle_status === "submitted"
                    ? "warning"
                    : worksheet.current_cycle_status === "closed"
                    ? "default"
                    : "default"
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="overline" color="text.secondary">
                Overall Risk
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <RiskScoreBadge
                  level={overallLevel}
                  exposure={overallExposure}
                  size="medium"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={2} sx={{ textAlign: { md: "right" } }}>
              {(isAdmin || isAssignedPM) && (
                <Button
                  variant="outlined"
                  startIcon={<TimelineIcon />}
                  onClick={openCycleDialog}
                  sx={{ mb: 1 }}
                  fullWidth
                >
                  Open new cycle
                </Button>
              )}
              <Button
                variant="text"
                size="small"
                startIcon={<HistoryIcon />}
                onClick={() =>
                  navigate(`/pms/risk-management/summary/${worksheet.project}`)
                }
                fullWidth
              >
                View summary
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderRowCells = (areaId: string, row: EditableRiskRow) => {
    const exposure = computeExposure(row.draftImpact, row.draftProbability);
    const level = levelFromExposure(exposure);
    const isExpanded = expandedRows.has(row.risk.id);
    return (
      <React.Fragment key={row.risk.id}>
        <TableRow hover sx={{ "& > *": { borderBottom: isExpanded ? "unset" : undefined } }}>
          <TableCell sx={{ width: 40, verticalAlign: "top" }}>
            <IconButton
              size="small"
              onClick={() => toggleRowExpanded(row.risk.id)}
            >
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          <TableCell sx={{ minWidth: 180, verticalAlign: "top" }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {row.risk.contributor_name ?? "—"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.risk.description}
            </Typography>
          </TableCell>
          <TableCell sx={{ width: 90 }}>
            <BoundedNumberInput
              value={row.draftImpact}
              min={1}
              max={10}
              disabled={!canEdit}
              onCommit={(next) =>
                updateRow(areaId, row.risk.id, { draftImpact: next })
              }
            />
          </TableCell>
          <TableCell sx={{ width: 90 }}>
            <BoundedNumberInput
              value={row.draftProbability}
              min={1}
              max={10}
              disabled={!canEdit}
              onCommit={(next) =>
                updateRow(areaId, row.risk.id, { draftProbability: next })
              }
            />
          </TableCell>
          <TableCell sx={{ width: 140 }}>
            <RiskScoreBadge level={level} exposure={exposure} />
          </TableCell>
          <TableCell sx={{ width: 150 }}>
            <TextField
              size="small"
              type="date"
              value={row.draftTargetDate}
              disabled={!canEdit}
              onChange={(e) =>
                updateRow(areaId, row.risk.id, { draftTargetDate: e.target.value })
              }
              InputLabelProps={{ shrink: true }}
            />
          </TableCell>
          <TableCell sx={{ width: 150 }}>
            <FormControl size="small" fullWidth disabled={!canEdit}>
              <Select
                value={row.draftStatus}
                onChange={(e) =>
                  updateRow(areaId, row.risk.id, {
                    draftStatus: e.target.value as RiskStatus,
                  })
                }
              >
                {STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </TableCell>
          <TableCell sx={{ width: 110, verticalAlign: "top" }}>
            <Stack direction="row" spacing={0}>
              <Tooltip title="Trend">
                <IconButton
                  size="small"
                  onClick={() =>
                    navigate(`/pms/risk-management/risk/${row.risk.id}/history`)
                  }
                >
                  <TimelineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Audit log">
                <IconButton
                  size="small"
                  onClick={() =>
                    navigate(`/pms/risk-management/risk/${row.risk.id}/audit`)
                  }
                >
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {canEdit && (
                <Tooltip title="Close risk">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleCloseRisk(row.risk)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell sx={{ py: 0, borderBottom: isExpanded ? undefined : "unset" }} colSpan={8}>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ py: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: "block" }}>
                      Factors for rating Impact & Probability
                    </Typography>
                    <SkillDocumentCommentBox
                      compact
                      hideTableControls
                      value={row.draftRatingFactors}
                      onChange={(val) =>
                        updateRow(areaId, row.risk.id, { draftRatingFactors: val })
                      }
                      disabled={!canEdit}
                      placeholder="Enter factors..."
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: "block" }}>
                      Specify Project Scenario justifying the impact and probability
                    </Typography>
                    <SkillDocumentCommentBox
                      compact
                      hideTableControls
                      value={row.draftScenarioJustification}
                      onChange={(val) =>
                        updateRow(areaId, row.risk.id, { draftScenarioJustification: val })
                      }
                      disabled={!canEdit}
                      placeholder="Describe the scenario..."
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: "block" }}>
                      Mitigation plan
                    </Typography>
                    <SkillDocumentCommentBox
                      compact
                      hideTableControls
                      value={row.draftMitigation}
                      onChange={(val) =>
                        updateRow(areaId, row.risk.id, { draftMitigation: val })
                      }
                      disabled={!canEdit}
                      placeholder="Mitigation plan..."
                    />
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  const renderArea = (area: RiskArea) => {
    const rows = rowsByArea[area.id] ?? [];
    const agg = areaAggregate(area.id);
    return (
      <Accordion key={area.id} defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              {area.name}
            </Typography>
            <Chip size="small" label={`${rows.length} risk(s)`} />
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Area avg
            </Typography>
            <RiskScoreBadge level={agg.level} exposure={agg.avg} />
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Risk Contributors</TableCell>
                  <TableCell>Impact</TableCell>
                  <TableCell>Probability</TableCell>
                  <TableCell>Level / Exposure</TableCell>
                  <TableCell>Target date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="body2" color="text.disabled" align="center">
                        No risks in this area yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => renderRowCells(area.id, row))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {canEdit && (
            <Box sx={{ mt: 1.5, textAlign: "right" }}>
              <Button
                size="small"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => openAdd(area.id)}
              >
                Add risk in {area.name}
              </Button>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 12, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!worksheet) {
    return (
      <Container maxWidth="md" sx={{ mt: 12 }}>
        <Alert severity="error">Worksheet not found.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 10, mb: 6 }}>
      <PageTitle>Risk Worksheet</PageTitle>

      {renderHeader()}

      {worksheet.is_locked && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This worksheet is locked. Editing is disabled.
        </Alert>
      )}
      {!canEdit && !worksheet.is_locked && (() => {
        // Differentiate the three reasons `canEdit` can be false so the user
        // sees an *actionable* message instead of a confusing "only the PM
        // can edit" prompt when they actually ARE the PM but the cycle is
        // simply not open yet.
        const cycleStatus = worksheet.current_cycle_status;
        const noOpenCycle = !cycleStatus || cycleStatus === "closed";
        const isReviewerOnly = !isAssignedPM;

        if (isReviewerOnly) {
          return (
            <Alert severity="info" sx={{ mb: 2 }}>
              You have read-only access. Only the assigned PM can edit risk
              data in this worksheet.
            </Alert>
          );
        }
        if (!worksheet.is_active) {
          return (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This worksheet has been disabled. An RMW Admin needs to
              re-enable it before editing is possible.
            </Alert>
          );
        }
        if (noOpenCycle) {
          return (
            <Alert severity="info" sx={{ mb: 2 }}>
              There is no open review cycle for this worksheet. Click{" "}
              <b>Open new cycle</b> above to start a new review and begin
              editing risks.
            </Alert>
          );
        }
        // Fallback (cycle is in some other state like `reappeared`).
        return (
          <Alert severity="info" sx={{ mb: 2 }}>
            The current cycle is in <b>{cycleStatus}</b> state and cannot be
            edited. Open a new cycle to continue.
          </Alert>
        );
      })()}

      {(template?.areas ?? []).map(renderArea)}

      {isAssignedPM && (
        <Paper
          elevation={3}
          sx={{
            position: "sticky",
            bottom: 16,
            mt: 3,
            p: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {dirtyRows.length > 0
              ? `${dirtyRows.length} unsaved change(s)`
              : "All changes saved."}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddCircleOutlineIcon />}
            disabled={!canEdit}
            onClick={() => openAdd()}
          >
            Add risk
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />
            }
            disabled={!canEdit || saving || !dirtyRows.length}
            onClick={handleDraftSave}
          >
            Save draft
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={
              submitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SendOutlinedIcon />
              )
            }
            disabled={!canEdit || submitting}
            onClick={handleSubmit}
          >
            {isCycleSubmitted ? "Re-submit ✓" : "Submit review"}
          </Button>
        </Paper>
      )}

      {/* Add risk dialog */}
      <Dialog open={addOpen} onClose={() => !addSaving && setAddOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add a new risk</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Risk Area</InputLabel>
              <Select
                label="Risk Area"
                value={addArea}
                onChange={(e) => {
                  setAddArea(e.target.value as string);
                  setAddContributor("");
                }}
              >
                {(template?.areas ?? []).map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth disabled={!addArea}>
              <InputLabel>Contributor</InputLabel>
              <Select
                label="Contributor"
                value={addContributor}
                onChange={(e) => setAddContributor(e.target.value as string)}
              >
                {contributorOptions.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {(() => {
              const selContrib = contributorOptions.find(
                (c) => c.id === addContributor
              );
              const selArea = template?.areas.find((a) => a.id === addArea);
              const rawFactors = selContrib?.factors
                || (selArea && selContrib ? getDefaultFactors(selArea.name, selContrib.name) : "");
              const facts = rawFactors
                ? rawFactors.split("\n").filter((f) => f.trim())
                : [];
              if (!facts.length) return null;
              return (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    bgcolor: "#e3f2fd",
                    borderColor: "info.200",
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="info.dark"
                    sx={{ display: "block", mb: 0.5 }}
                  >
                    Assessment factors for &ldquo;{selContrib?.name}&rdquo;:
                  </Typography>
                  <Box
                    component="ul"
                    sx={{
                      m: 0,
                      pl: 2.5,
                      "& li": {
                        fontSize: "0.8rem",
                        color: "text.secondary",
                        py: 0.15,
                      },
                    }}
                  >
                    {facts.map((f, fi) => (
                      <li key={fi}>{f}</li>
                    ))}
                  </Box>
                </Paper>
              );
            })()}
            <TextField
              label="Risk description"
              fullWidth
              multiline
              minRows={3}
              value={addDescription}
              onChange={(e) => setAddDescription(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={addSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddRisk}
            disabled={addSaving}
            startIcon={addSaving ? <CircularProgress size={16} /> : null}
          >
            Add risk
          </Button>
        </DialogActions>
      </Dialog>

      {/* New cycle dialog */}
      <Dialog
        open={cycleOpen}
        onClose={() => !cycleSaving && setCycleOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Open new review cycle</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Cycle name"
              fullWidth
              value={cycleName}
              onChange={(e) => setCycleName(e.target.value)}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Period start"
                type="date"
                value={cycleStart}
                onChange={(e) => setCycleStart(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Period end"
                type="date"
                value={cycleEnd}
                onChange={(e) => setCycleEnd(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <FormControlLabel
              control={
                <Checkbox
                  checked={carryForward}
                  onChange={(e) => setCarryForward(e.target.checked)}
                />
              }
              label="Carry forward open risks from previous cycle"
            />
            <Divider />
            <Typography variant="caption" color="text.secondary">
              When enabled, every risk on this worksheet whose latest status is
              not <i>closed</i> is cloned into the new cycle so you start with
              the previous impact / probability / mitigation in place.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCycleOpen(false)} disabled={cycleSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateCycle}
            disabled={cycleSaving}
            startIcon={cycleSaving ? <CircularProgress size={16} /> : null}
          >
            Open cycle
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProjectRMWWorkspace;
